import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import type { UpstoxCandle } from '../services/candle-service'
import { fetchHistoricalCandles } from '../services/candle-service'
import type { MarketData } from '../types'

interface UseStrikeCandlesProps {
    selectedCeStrike: number
    selectedPeStrike: number
    activeMarketData: MarketData | null
    timeframe: string
    symbol: string
}

export function useStrikeCandles({
    selectedCeStrike,
    selectedPeStrike,
    activeMarketData,
    timeframe,
    symbol,
}: UseStrikeCandlesProps) {
    // 🛡️ IRONCLAD: Stable empty return to prevent any downstream computation
    const EMPTY_CANDLES: UpstoxCandle[] = []
    // Extract primitive instrument keys — prevents re-triggering from activeMarketData updating every 2s
    const { ceKey, peKey } = useMemo(() => {
        const options = activeMarketData?.options || (activeMarketData as any)?.optionChain || []
        if (!options || options.length === 0) return { ceKey: '', peKey: '' }

        const ceOption = options.find((o: any) => o.strike === selectedCeStrike)
        const peOption = options.find((o: any) => o.strike === selectedPeStrike)

        // Check ALL possible key names: worker uses callInstrumentKey/putInstrumentKey,
        // snapshot-transformer uses ceInstrumentKey/peInstrumentKey, and legacy uses nested ce/pe objects
        let ceKey =
            (ceOption as any)?.ceInstrumentKey ||
            (ceOption as any)?.callInstrumentKey ||
            (ceOption as any)?.ce?.instrumentKey ||
            ''
        let peKey =
            (peOption as any)?.peInstrumentKey ||
            (peOption as any)?.putInstrumentKey ||
            (peOption as any)?.pe?.instrumentKey ||
            ''

        // If still empty, try nested original payload (raw Upstox format)
        if (!ceKey && (ceOption as any)?.callOptionData) ceKey = (ceOption as any).callOptionData.instrumentKey || ''
        if (!peKey && (peOption as any)?.putOptionData) peKey = (peOption as any).putOptionData.instrumentKey || ''

        return { ceKey, peKey }
    }, [activeMarketData, selectedCeStrike, selectedPeStrike])

    // 🛡️ IRONCLAD: Early-exit when no symbol — prevents useQuery from even initializing fetch logic
    const isDaily = timeframe === 'day' || timeframe === 'week' || timeframe === 'month'
    const daysToFetch = isDaily ? 365 : 10
    const enabled = !!symbol && (!!ceKey || !!peKey) && selectedCeStrike > 0

    // Auto-refresh every 60s for 1minute timeframe, otherwise static (historical candles don't change)
    const refetchInterval = timeframe === '1minute' ? 60_000 : (false as const)

    const ceQuery = useQuery<UpstoxCandle[]>({
        queryKey: ['strike-candles', 'ce', symbol, ceKey, timeframe],
        queryFn: () => (ceKey ? fetchHistoricalCandles(symbol, daysToFetch, timeframe, ceKey) : []),
        enabled: enabled && !!ceKey && selectedCeStrike > 0,
        refetchInterval,
        staleTime: timeframe === '1minute' ? 55_000 : 5 * 60_000, // 55s for intraday, 5m for daily
        refetchOnWindowFocus: false,
        placeholderData: (prev) => prev, // keep old data visible during background refetch
    })

    const peQuery = useQuery<UpstoxCandle[]>({
        queryKey: ['strike-candles', 'pe', symbol, peKey, timeframe],
        queryFn: () => (peKey ? fetchHistoricalCandles(symbol, daysToFetch, timeframe, peKey) : []),
        enabled: enabled && !!peKey && selectedPeStrike > 0,
        refetchInterval,
        staleTime: timeframe === '1minute' ? 55_000 : 5 * 60_000,
        refetchOnWindowFocus: false,
        placeholderData: (prev) => prev,
    })

    return {
        ceCandles: ceQuery.data || EMPTY_CANDLES,
        peCandles: peQuery.data || EMPTY_CANDLES,
        loading: ceQuery.isLoading || peQuery.isLoading,
    }
}
